import {partition} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import assert from 'assert';
import React from 'react';

import {CONSTANT_DOMAIN_LABEL_COLOR, VARIABLE_DOMAIN_LABEL_COLOR} from 'antibodies/AntibodyConstants';
import {
  AntibodyFormat,
  AntibodyFormatChainDomain,
  AntibodyFormatDomain,
  AntibodyFormatDomainVisualization,
  AntibodyFormatHingeDomain,
  AntibodyFormatLinkerDomain,
  ChainDomainSizing,
} from 'antibodies/AntibodyTypedefs';
import {domainColor, formatToSortedDistinctChains, isConstantDomain} from 'antibodies/AntibodyUtils';
import AntibodyDomain from 'antibodies/visualization/AntibodyDomain';
import AntibodyDomainLabel from 'antibodies/visualization/AntibodyDomainLabel';
import AntibodyHinge from 'antibodies/visualization/AntibodyHinge';
import AntibodyLinker from 'antibodies/visualization/AntibodyLinker';
import InteractiveComponentWrapper, {
  AntibodyInteractivityProps,
} from 'antibodies/visualization/InteractiveComponentWrapper';
import BenchlingConfig from 'BenchlingConfig';
import {AntibodyDomainType} from 'graphql-client/__generated/graphqlGlobalTypes';
import sortedBy from 'util/sortedBy';

function assertNoJoiners(
  domains: Array<AntibodyFormatDomain>
): asserts domains is Array<
  AntibodyFormatChainDomain | AntibodyFormatHingeDomain | AntibodyFormatLinkerDomain
> {
  assert(
    domains.every(({type}) => type !== AntibodyDomainType.J),
    "we don't know how to make joiners yet"
  );
}

/**
 * Given transform params, get the actual transform string that should be given to the SVG `g` wrapper for
 * this domain.
 * @param translate How far to move the domain, both horizontally and vertically
 * @param rotate How many degrees clockwise to rotate the domain
 * @param reflect Whether to reflect the domain horizontally
 */
function getDomainTransform(display: AntibodyFormatDomainVisualization): string {
  const {translate, rotate, reflectX, reflectY} = display;
  assert(!reflectY, 'reflectY is not supported yet');
  const translateX = translate.x;
  const translateY = translate.y;
  const translation = `translate(${translateX}, ${translateY})`;
  // Rotate around the top left corner of the bounding box of the domain, not around the origin of the SVG.
  const rotation = `rotate(${rotate} ${translateX} ${translateY})`;
  const reflection = reflectX ? 'scale(-1, 1)' : '';
  return [rotation, translation, reflection].join(' ');
}

/**
 * Given transform params, get the actual transform string that should be given to the SVG `g` wrapper for
 * this domain label.
 *
 * Footgun alert: This function assumes that this transform is being applied to an element with SVG styles
 * of transform-box: fill-box and transform-origin: center. This is because SVG does not alter the transformed
 * element's own coordinate system, only that of the transformed element's children. There is no easy way to
 * rotate an element around its own center in SVG, so we do it with CSS instead.
 *
 * @param rotate How many degrees clockwise the domain was rotated. This rotation will be reversed for the
 *               label.
 * @param height How tall the domain is
 * @param width How wide the domain is
 */
function getLabelTransform(
  {rotate}: AntibodyFormatDomainVisualization,
  {height, width}: ChainDomainSizing
): string {
  const labelHeight = height / 4;
  const labelWidth = (3 * width) / 4;
  const translateX = width / 2 - labelWidth / 2;
  const translateY = height / 2 - labelHeight / 2;
  const translation = `translate(${translateX}, ${translateY})`;
  // Rotate the _opposite_ direction from the domain. We always want to render this text horizontally.
  const rotation = rotate != null ? `rotate(${-rotate})` : '';
  return [translation, rotation].join(' ');
}

/*
 * Returns whether a particular domain is active (hovered or selected) in the antibody map.
 * All domains referenced by a distinct chain are considered active, if the chain is hovered or selected
 * (because an active chain is reflected through updates to the visualization of its corresponding domains).
 */
export function isDomainActive({
  format,
  domainRef,
  interactedRef,
}: {
  format: AntibodyFormat;
  domainRef: number;
  interactedRef: number | null;
}): boolean {
  if (interactedRef == null) {
    return false;
  }

  // If the interacted component is a domain, all domains with the same type and specificity should be considered active
  const maybeFormatDomain = format.domains.find((domain) => domain.ref === interactedRef);
  if (maybeFormatDomain != null) {
    const allMatchingDomainRefs = format.domains
      .filter(
        (domain) =>
          domain.type === maybeFormatDomain.type && domain.specificity === maybeFormatDomain.specificity
      )
      .map((domain) => domain.ref);
    return allMatchingDomainRefs.includes(domainRef);
  }

  assert(
    BenchlingConfig.MOLBIO_ENABLE_ANTIBODY_CHAINS,
    'Chains must be enabled to interact with chain components'
  );
  // If the active component is a chain, all domains on all chains with the same type and specificity should be considered active
  const formatChain = formatToSortedDistinctChains(format).find((chain) =>
    chain.refs.includes(interactedRef)
  );
  assert(formatChain != null, 'Interacted ref must be a valid component ref');
  return formatChain.domainRefsPerChain.flat().includes(domainRef);
}

/**
 * Get whether the domain should be displayed as deemphasized.
 * @param format The format for the antibody.
 * @param domainRef Ref of domain in question.
 * @param interactivity Interactivity state for the antibody.
 */
function getDeemphasized({
  format,
  domainRef,
  interactivity,
}: {
  format: AntibodyFormat;
  domainRef: number;
  interactivity?: AntibodyInteractivityProps;
}): boolean {
  if (interactivity?.selectedRef != null) {
    return !isDomainActive({format, domainRef, interactedRef: interactivity?.selectedRef});
  }
  return (
    interactivity?.hoveredRef != null &&
    !isDomainActive({format, domainRef, interactedRef: interactivity?.hoveredRef})
  );
}

function safeGetChainIndex(ref: number, indexByRef: Record<number, number>): number {
  const index = indexByRef[ref];
  assert(index !== undefined, `Domain ref ${ref} not found in chains`);
  return index;
}

export interface AntibodyProps {
  format: AntibodyFormat;
  interactivity?: AntibodyInteractivityProps;
  showLabels?: boolean;
}

/**
 * An SVG visualization of an antibody.
 * This is an SVG component, so all units are 'abstract user units', relative to the SVG viewbox.
 * @param format The format for the antibody, which includes visualization data (display and sizing information).
 * @param interactivity Interactivity state for the antibody. If not passed, the antibody viz will be a static
 *                      SVG.
 * @param interactivity.hoveredRef Which domain ref is currently hovered, if any.
 * @param interactivity.proteinIdByRef A map from domain ref to associated protein API ID (including inaccessible ones).
 * @param interactivity.inaccessibleProteinIdsByRef A map from domain ref to each inaccessible protein API ID.
 * @param interactivity.selectedRef Which domain ref is currently selected, if any
 * @param interactivity.setHoveredRef A function to set which domain ref is currently hovered.
 * @param interactivity.setSelectedRefs A function to set which domain ref is currently selected.
 * @param showLabels Whether to show labels on chain domains.
 */
export default function Antibody({format, interactivity, showLabels}: AntibodyProps): JSX.Element {
  const domainVisualizationsByRef: Record<number, AntibodyFormatDomainVisualization> =
    format.visualization.domainVisualizations.reduce<Record<number, AntibodyFormatDomainVisualization>>(
      (acc, domainViz) => {
        acc[domainViz.ref] = domainViz;
        return acc;
      },
      {}
    );

  assert(
    format.domains.every(({type}) => type !== AntibodyDomainType.J),
    "we don't know how to make joiners yet"
  );
  const domainRefToDistinctChainIndex = formatToSortedDistinctChains(format).reduce<Record<number, number>>(
    (acc, chain, idx) => {
      for (const domainRef of chain.domainRefsPerChain.flat()) {
        acc[domainRef] = idx;
      }
      return acc;
    },
    {}
  );

  assertNoJoiners(format.domains);
  const [hinges, nonHinges] = partition<
    AntibodyFormatHingeDomain,
    AntibodyFormatChainDomain | AntibodyFormatLinkerDomain
  >(format.domains, ({type}) => type === AntibodyDomainType.H);
  const [linkers, chainDomains] = partition<AntibodyFormatLinkerDomain, AntibodyFormatChainDomain>(
    nonHinges,
    ({type}) => type === AntibodyDomainType.L
  );
  // Sort chain domains by selected status. SVG z-indexing is a strict function of what is rendered first. As
  // such, anything with a stroke should be rendered last.
  const sortedChainDomains = sortedBy(chainDomains, [
    ({ref}) => (interactivity?.selectedRef === ref ? 1 : -1),
  ]);

  const {chainDomainSizing, connectorSizing, svgSizing} = format.visualization.sizing;
  return (
    <>
      <svg
        height="100%"
        width="100%"
        viewBox={`0 0 ${svgSizing.svgViewboxWidth} ${svgSizing.svgViewboxHeight}`}
      >
        {hinges.map((hinge) => {
          const display = domainVisualizationsByRef[hinge.ref];
          assert(
            display.__typename === 'HingeDomainVisualizationNode',
            'Received non-hinge display params for a hinge domain'
          );
          const {stroke} = domainColor({
            distinctDomain: {
              type: hinge.type,
              specificity: hinge.specificity,
              chainIndex: safeGetChainIndex(hinge.ref, domainRefToDistinctChainIndex),
            },
            deemphasized: getDeemphasized({format, domainRef: hinge.ref, interactivity}),
            selected: isDomainActive({
              format,
              domainRef: hinge.ref,
              interactedRef: interactivity?.selectedRef ?? null,
            }),
          });
          return (
            <g key={hinge.ref} transform={getDomainTransform(display)}>
              <InteractiveComponentWrapper componentRef={hinge.ref} interactivity={interactivity}>
                <AntibodyHinge
                  domainVisualization={display}
                  connectorSizing={connectorSizing}
                  color={stroke}
                  proteinId={interactivity?.proteinIdsByRef[hinge.ref] ?? null}
                  inaccessible={interactivity?.inaccessibleProteinIdsByRef[hinge.ref] != null}
                />
              </InteractiveComponentWrapper>
            </g>
          );
        })}
        {linkers.map((linker) => {
          const display = domainVisualizationsByRef[linker.ref];
          assert(
            display.__typename === 'LinkerDomainVisualizationNode',
            'Received non-linker display params for a linker domain'
          );
          const {stroke} = domainColor({
            distinctDomain: {
              type: linker.type,
              specificity: linker.specificity,
              chainIndex: safeGetChainIndex(linker.ref, domainRefToDistinctChainIndex),
            },
            deemphasized: getDeemphasized({format, domainRef: linker.ref, interactivity}),
            selected: isDomainActive({
              format,
              domainRef: linker.ref,
              interactedRef: interactivity?.selectedRef ?? null,
            }),
          });
          return (
            <g key={linker.ref} transform={getDomainTransform(display)}>
              <InteractiveComponentWrapper componentRef={linker.ref} interactivity={interactivity}>
                <AntibodyLinker
                  domainVisualization={display}
                  connectorSizing={connectorSizing}
                  color={stroke}
                  proteinId={interactivity?.proteinIdsByRef[linker.ref] ?? null}
                  inaccessible={interactivity?.inaccessibleProteinIdsByRef[linker.ref] != null}
                />
              </InteractiveComponentWrapper>
            </g>
          );
        })}
        {sortedChainDomains.map((domain) => {
          const display = domainVisualizationsByRef[domain.ref];
          assert(
            display.__typename === 'BlockDomainVisualizationNode',
            'Received non-chain display params for a chain domain'
          );
          const {fill, stroke} = domainColor({
            distinctDomain: {
              type: domain.type,
              specificity: domain.specificity,
              chainIndex: safeGetChainIndex(domain.ref, domainRefToDistinctChainIndex),
            },
            deemphasized: getDeemphasized({format, domainRef: domain.ref, interactivity}),
            selected: isDomainActive({
              format,
              domainRef: domain.ref,
              interactedRef: interactivity?.selectedRef ?? null,
            }),
          });
          return (
            <g key={domain.ref} transform={getDomainTransform(display)}>
              <InteractiveComponentWrapper componentRef={domain.ref} interactivity={interactivity}>
                <AntibodyDomain
                  domainVisualization={display}
                  chainDomainSizing={chainDomainSizing}
                  fillColor={fill}
                  proteinId={interactivity?.proteinIdsByRef[domain.ref] ?? null}
                  strokeColor={stroke}
                  inaccessible={interactivity?.inaccessibleProteinIdsByRef[domain.ref] != null}
                />
                {showLabels && (
                  <g
                    transform={getLabelTransform(display, chainDomainSizing)}
                    className={css(styles.labelWrapper)}
                  >
                    <AntibodyDomainLabel
                      color={
                        isConstantDomain(domain.type)
                          ? CONSTANT_DOMAIN_LABEL_COLOR
                          : VARIABLE_DOMAIN_LABEL_COLOR
                      }
                      height={chainDomainSizing.height / 4}
                      text={domain.type}
                      width={(3 * chainDomainSizing.width) / 4}
                    />
                  </g>
                )}
              </InteractiveComponentWrapper>
            </g>
          );
        })}
      </svg>
    </>
  );
}

const styles = StyleSheet.create({
  labelWrapper: {
    transformBox: 'fill-box',
    transformOrigin: 'center',
  },
});
